import { animate, style, transition, trigger } from '@angular/animations';
import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { chunk, cloneDeep } from 'lodash';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import {
    FeatureFlags,
    IClaims,
    Modal,
    SidebarMenuItem,
    SidebarProfile
} from '../../../models/common';
import { ClaimService } from '../../../services/claim/claim.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { ModalService } from '../../../services/modal/modal.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { SidebarService } from '../../../services/sidebar/sidebar.service';
import { getUrlFromRouterLink } from '../../../utilities/route.helpers';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';

@LifecycleAware()
@Component({
    selector: 'lsq-sidebar',
    template: `
        <div class="sidebar" [ngClass]="isOpen ? '' : 'closed'">
            <ng-template #dropdownTemplate let-menuItem="menuItem">
                <lsq-dropdown
                    *ngIf="getDropdownChildren(menuItem).length"
                    [show]="menuItem.getState().showingDropdown"
                    [hasHeader]="false"
                    triangle="left"
                    [headerIsButton]="true"
                    parentClass="sidebar-dropdown"
                    [shouldAnimate]="true"
                >
                    <ul class="list-unstyled text-center">
                        <li *ngFor="let child of getDropdownChildren(menuItem); let x = index">
                            <a
                                (click)="goToPage($event, child)"
                                [ngClass]="isRouteActive(child) ? 'active' : ''"
                                (mouseenter)="showDropdown(child, x)"
                                (mouseleave)="showDropdown(child, x, false)"
                            >
                                <span class="icon-wrapper"
                                    ><i class="sidebar-icon fa" [ngClass]="child.icon"></i
                                ></span>
                                {{ child.label }}
                                <ng-container
                                    *ngTemplateOutlet="
                                        dropdownTemplate;
                                        context: { menuItem: child }
                                    "
                                >
                                </ng-container>
                            </a>
                        </li>
                    </ul>
                </lsq-dropdown>
            </ng-template>

            <nav class="sidebar-inner">
                <div
                    class="sidebar-control"
                    [ngClass]="{ opened: isOpen }"
                    (click)="toggleMenu(isOpen, true)"
                >
                    <span *ngIf="!isOpen"><i class="fas fa-arrow-right"></i></span>
                    <span *ngIf="isOpen" class="text-right"
                        ><i class="fas fa-arrow-to-left"></i
                    ></span>
                </div>
                <div class="sidebar-header-profile" *ngIf="profile && isOpen">
                    <p class="company"><i class="fal fa-building"></i> {{ profile.company }}</p>
                    <div class="employees" *ngIf="profileEmployees?.length > 0">
                        <div class="employee-row" *ngFor="let set of profileEmployees">
                            <div class="employee" *ngFor="let item of set">
                                <span class="label">{{ item.label }}:</span> {{ item.name
                                }}<span class="divider">|</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    *ngIf="!profile && isOpen"
                    class="sidebar-header-app"
                    [ngClass]="{ 'single-app': applicationMenuItems.length === 1 }"
                    (click)="handleAppHeaderClick()"
                >
                    <div class="application">
                        <span>
                            <i class="fal fa-columns"></i>
                        </span>
                        <span class="app-name" *ngIf="isOpen">
                            <span
                                *ngFor="let applicationMenuItem of applicationMenuItems"
                                routerLinkActive="active"
                            >
                                <a
                                    class="selected-application"
                                    [routerLink]="applicationMenuItem.routerLink"
                                >
                                    {{ applicationMenuItem.label }}
                                </a>
                            </span>
                        </span>
                        <div class="caret" *ngIf="isOpen && applicationMenuItems.length > 1">
                            <span *ngIf="!isAppDrawerExpanded"
                                ><i class="fal fa-angle-right"></i
                            ></span>
                            <span *ngIf="isAppDrawerExpanded"
                                ><i class="fal fa-angle-down"></i
                            ></span>
                        </div>
                    </div>
                </div>
                <div *ngIf="isAppDrawerExpanded">
                    <ul class="nav-items application-menu-items">
                        <ng-container *ngFor="let applicationMenuItem of applicationMenuItems">
                            <li routerLinkActive="active">
                                <a
                                    class="application-menu-item"
                                    [routerLink]="applicationMenuItem.routerLink"
                                >
                                    {{ applicationMenuItem.label }}
                                </a>
                            </li>
                        </ng-container>
                    </ul>
                </div>
                <ul class="nav-items">
                    <li
                        *ngFor="let item of filteredMenuItems; let i = index"
                        [title]="!isOpen ? item.label : ''"
                    >
                        <a
                            *ngIf="!item.children?.length"
                            [routerLink]="item.routerLink"
                            routerLinkActive="active"
                        >
                            <span class="icon-wrapper"
                                ><i class="sidebar-icon fa" [ngClass]="item.icon"></i
                            ></span>
                            <span class="label">{{ item.label }}</span>
                        </a>
                        <a
                            *ngIf="item.children && item.children.length > 0"
                            (mouseenter)="showDropdown(item, i)"
                            (mouseleave)="showDropdown(item, i, false)"
                            [ngClass]="isRouteActive(item) ? 'active' : ''"
                            (click)="
                                !getAccordionChildren(item).length
                                    ? goToPage($event, item)
                                    : toggleAccordion(item)
                            "
                        >
                            <span class="icon-wrapper">
                                <i class="sidebar-icon fa" [ngClass]="item.icon"></i>
                            </span>
                            <span class="label">{{ item.label }}</span>
                            <!-- Font awesome appears to be caching classes so we have to swap like this-->
                            <span
                                [ngClass]="{ 'is-collapsed': !isOpen }"
                                *ngIf="
                                    isOpen &&
                                    getAccordionChildren(item).length &&
                                    item.getState().showingAccordion
                                "
                            >
                                <i class="fa sidebar-accordion-toggle fa-angle-down"></i
                            ></span>
                            <span
                                [ngClass]="{ 'is-collapsed': !isOpen }"
                                *ngIf="
                                    isOpen &&
                                    getAccordionChildren(item).length &&
                                    !item.getState().showingAccordion
                                "
                            >
                                <i class="fa sidebar-accordion-toggle fa-angle-right"></i
                            ></span>
                            <ng-container
                                *ngTemplateOutlet="dropdownTemplate; context: { menuItem: item }"
                            >
                            </ng-container>
                        </a>
                        <ul
                            *ngIf="
                                getAccordionChildren(item).length &&
                                item.getState().showingAccordion
                            "
                            class="sidebar-sub-list"
                            [@dropIn]
                        >
                            <li
                                *ngFor="let child of getAccordionChildren(item)"
                                [ngClass]="{ 'is-collapsed': !isOpen }"
                                [title]="!isOpen ? child.label : ''"
                            >
                                <a
                                    [routerLink]="child.routerLink"
                                    routerLinkActive="active"
                                    [ngClass]="{ 'accordion-sub-list': isOpen }"
                                    (mouseenter)="showDropdown(child, i)"
                                    (mouseleave)="showDropdown(child, i, false)"
                                >
                                    <span class="icon-wrapper"
                                        ><i class="sidebar-icon fa" [ngClass]="child.icon"></i
                                    ></span>
                                    <span class="label">{{ child.label }}</span>
                                    <ng-container
                                        *ngTemplateOutlet="
                                            dropdownTemplate;
                                            context: { menuItem: child }
                                        "
                                    >
                                    </ng-container>
                                </a>
                            </li>
                        </ul>
                    </li>
                </ul>
            </nav>
        </div>
    `,
    styleUrls: ['./sidebar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('dropIn', [
            transition(':enter', [style({ height: 0 }), animate(200, style({ height: '*' }))]),
            transition(':leave', [style({ height: '*' }), animate(200, style({ height: 0 }))])
        ])
    ]
})
export class LsqSidebarComponent implements OnInit, OnDestroy, OnChanges {
    @Input() public profile?: SidebarProfile;
    @Input() public menuItems?: SidebarMenuItem[] = [];
    @Input() public navigationGroupName: string;
    @Input() public applicationMenuItems?: SidebarMenuItem[] = [];

    public profileEmployees: { name: string; label: string }[][];
    public isOpen: boolean;
    public isAppDrawerExpanded = false;
    public applicationRoutes?: string[];
    public filteredMenuItems: SidebarMenuItem[] = [];

    private _navLinkIndexesHovered: number[] = [];
    private _userToggledSidebar = false;

    constructor(
        private responsiveService: ResponsiveService,
        private sidebarService: SidebarService,
        private modalService: ModalService,
        private router: Router,
        public activatedRoute: ActivatedRoute,
        public claimService: ClaimService,
        public featureFlagService: FeatureFlagService
    ) {
        this.sidebarService.toggleStream.pipe(forLifeOf(this)).subscribe((isOpen) => {
            this.isOpen = isOpen;
        });

        this.responsiveService.lgBelow
            .pipe(forLifeOf(this), distinctUntilChanged())
            .subscribe((isLgOrBelow) => {
                if (!this._userToggledSidebar) {
                    this.toggleMenu(isLgOrBelow, false);
                }
            });
        this.sidebarService.existsSubject.next(true);
    }

    public ngOnInit(): void {
        this._setupSidebarProfile();

        combineLatest(this.claimService.claimStream, this.featureFlagService.featureFlagStream)
            .pipe(
                forLifeOf(this),
                startWith([this.claimService.claims, this.featureFlagService.featureFlags])
            )
            .subscribe(([claims, featureFlags]) => {
                this.filteredMenuItems = this.filterOutMenuItems(
                    this.menuItems,
                    claims as IClaims,
                    featureFlags as FeatureFlags
                );
            });
    }

    public ngOnDestroy(): void {
        this.sidebarService.existsSubject.next(false);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.menuItems) {
            this.filteredMenuItems = this.filterOutMenuItems(
                this.menuItems,
                this.claimService.claims,
                this.featureFlagService.featureFlags
            );
        }
    }

    private filterOutMenuItems(
        menuItems: SidebarMenuItem[],
        claims: IClaims,
        featureFlags: FeatureFlags
    ): SidebarMenuItem[] {
        const filteredMenuItems = [];
        // We need to deep clone so we don't affect the original when setting children.
        const copiedMenuItems = cloneDeep(menuItems);
        copiedMenuItems.forEach((menuItem) => {
            let isBlocked = false;

            if (menuItem.claims && menuItem.claims.length > 0) {
                isBlocked = menuItem.claims.every((claim) => !claims[claim]);
            }

            if (menuItem.featureFlags && menuItem.featureFlags.length > 0) {
                menuItem.featureFlags.forEach((featureFlag) => {
                    if (!featureFlags || !featureFlags[featureFlag]) {
                        isBlocked = true;
                    }
                });
            }

            if (menuItem.children) {
                menuItem.children = this.filterOutMenuItems(
                    menuItem.children,
                    claims,
                    featureFlags
                );
            }

            if (!isBlocked) {
                filteredMenuItems.push(menuItem);
            }
        });
        return filteredMenuItems;
    }

    private _setupSidebarProfile(): void {
        const profileEmployees = [];
        if (this.profile) {
            if (this.profile.bdo) {
                profileEmployees.push({ label: 'BDO', name: this.profile.bdo });
            }

            if (this.profile.underwriter) {
                profileEmployees.push({ label: 'UW', name: this.profile.underwriter });
            }

            if (this.profile.accountExecutive) {
                profileEmployees.push({ label: 'AE', name: this.profile.accountExecutive });
            }

            if (this.profile.accountManager) {
                profileEmployees.push({ label: 'AM', name: this.profile.accountManager });
            }
        }
        this.profileEmployees = chunk(profileEmployees, 2);
    }

    public toggleMenu(isOpen: boolean, fromUserClick: boolean): void {
        this.isAppDrawerExpanded = false;
        this._userToggledSidebar = fromUserClick;
        this.sidebarService.toggle(!isOpen);
    }

    public displayModal(options: Modal): void {
        this.modalService.showModal(options);
    }

    public goToPage(event: MouseEvent, menuItem: SidebarMenuItem): void {
        event.stopImmediatePropagation();
        if (menuItem.modal) {
            this.displayModal(menuItem.modal);
        }
        if (menuItem.routerLink) {
            this.router.navigate(menuItem.routerLink);
        }
        event.preventDefault();
    }

    public isRouteActive(menuItem: SidebarMenuItem): boolean {
        if (
            !menuItem.getState().showingAccordion &&
            this.isOpen &&
            menuItem.children &&
            menuItem.children.length
        ) {
            return menuItem.children.some(
                (childItem) =>
                    childItem.routerLink &&
                    this.router.isActive(getUrlFromRouterLink(childItem.routerLink), true)
            );
        }
        return menuItem.routerLink
            ? this.router.isActive(getUrlFromRouterLink(menuItem.routerLink), true)
            : false;
    }

    public toggleDropdown(menuItem: SidebarMenuItem): void {
        menuItem.getState().showingDropdown = !menuItem.getState().showingDropdown;
    }

    public toggleAccordion(menuItem: SidebarMenuItem): void {
        menuItem.getState().showingAccordion = !menuItem.getState().showingAccordion;
    }

    public getDropdownChildren(menuItem: SidebarMenuItem): SidebarMenuItem[] {
        const dropdownItems: SidebarMenuItem[] = [];
        if (menuItem.children && menuItem.children.length) {
            menuItem.children.forEach((child) => {
                if (child.modal) {
                    dropdownItems.push(child);
                }
            });
        }

        return dropdownItems;
    }

    public getAccordionChildren(menuItem: SidebarMenuItem): SidebarMenuItem[] {
        const accordionItems: SidebarMenuItem[] = [];
        if (menuItem.children && menuItem.children.length) {
            menuItem.children.forEach((child) => {
                if (child.routerLink) {
                    accordionItems.push(child);
                }
            });
        }

        return accordionItems;
    }

    public isSomeNavLinkHovered(): boolean {
        return this._navLinkIndexesHovered.length > 0;
    }

    public handleNavLinkMouseEnter(index: number): void {
        if (this._navLinkIndexesHovered.indexOf(index) === -1) {
            this._navLinkIndexesHovered.push(index);
        }
    }

    public handleNavLinkMouseLeave(menuItem: SidebarMenuItem, index: number): void {
        const linkIndex = this._navLinkIndexesHovered.indexOf(index);
        if (
            !(
                menuItem.getState().showingDropdown &&
                menuItem.children &&
                menuItem.children.length
            ) &&
            linkIndex > -1
        ) {
            this._navLinkIndexesHovered.splice(linkIndex, 1);
        }
    }

    public showDropdown(menuItem: SidebarMenuItem, index: number, show = true): void {
        menuItem.getState().showingDropdown = show;
        if (menuItem.getState().showingDropdown && menuItem.children && menuItem.children.length) {
            this.handleNavLinkMouseEnter(index);
        } else {
            this.handleNavLinkMouseLeave(menuItem, index);
        }
    }

    public handleAppHeaderClick(): void {
        if (this.applicationMenuItems.length > 1) {
            this.isAppDrawerExpanded === true
                ? (this.isAppDrawerExpanded = false)
                : (this.isAppDrawerExpanded = true);
        }
    }
}
